import { createRouter, createWebHistory } from 'vue-router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

// Configure NProgress
NProgress.configure({ showSpinner: false, speed: 400, minimum: 0.1 });

const routes = [
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/auth/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/accept-invite',
    name: 'AcceptInvite',
    component: () => import('../views/auth/AcceptInvite.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/auth/ForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/files',
    name: 'Files',
    component: () => import('../views/FileManager.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/transfers',
    name: 'TransferMonitor',
    component: () => import('../views/TransferMonitor.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('../views/Users.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/roles',
    name: 'ManageRoles',
    component: () => import('../views/RolesPermissions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/network',
    name: 'Network',
    component: () => import('../views/Network.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/storage',
    name: 'Storage',
    component: () => import('../views/Storage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/storage/disks',
    name: 'StorageDisks',
    component: () => import('../views/StorageDisks.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/storage/backups',
    name: 'BackupRestore',
    component: () => import('../views/BackupRestore.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/storage/quotas',
    name: 'Quotas',
    component: () => import('../views/Quotas.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/storage/file-types',
    name: 'FileTypes',
    component: () => import('../views/FileTypes.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/security',
    name: 'Security',
    component: () => import('../views/Security.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('../views/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('../views/Reports.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('../views/Billing.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/developer/tokens',
    name: 'ApiTokens',
    component: () => import('../views/Developer/ApiTokens.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/workspaces',
    name: 'Workspaces',
    component: () => import('../views/WorkspaceList.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/workspaces/:id',
    name: 'WorkspaceDetail',
    component: () => import('../views/WorkspaceDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/shares',
    name: 'Shares',
    component: () => import('../views/Shares.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/s/:token',
    name: 'ShareLink',
    component: () => import('../views/ShareLink.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/transfer',
    name: 'TransferWizard',
    component: () => import('../views/TransferWizard.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/departments',
    name: 'Departments',
    component: () => import('../views/Departments.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/upload-wizard',
    name: 'UploadWizard',
    component: () => import('../views/UploadWizard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../views/Settings.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/settings/roles',
    name: 'RolesPermissions',
    component: () => import('../views/RolesPermissions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/settings/notifications',
    name: 'Notifications',
    component: () => import('../views/Notifications.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/system-logs',
    name: 'SystemLogs',
    component: () => import('../views/SystemLogs.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

import { useAuthStore } from '../store/auth';

router.beforeEach(async (to, from, next) => {
  NProgress.start();
  const token = localStorage.getItem('auth_token');
  
  if (to.meta.requiresAuth && !token) {
    next('/login');
  } else {
    if (token) {
      const authStore = useAuthStore();
      if (!authStore.user) {
        await authStore.fetchUser();
        // If fetchUser failed and cleared the token, redirect to login
        if (!authStore.token && to.meta.requiresAuth) {
          return next('/login');
        }
      }
      
      // Admin guard
      if (to.meta.requiresAdmin && !authStore.isAdmin) {
        return next('/');
      }
    }
    next();
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
